import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import { useCookies } from 'vue3-cookies'
import mitt from 'mitt'
import { newVersionAvailable, reloadTo, isChunkLoadError, reloadForChunkError } from '@/utils/appVersion'

const { cookies } = useCookies();

const emitter = mitt();

const routes = [
  {
    path: '',
    name: '',
    component: () => import('../views/HomeView.vue')
  },
  {
    path: '/',
    name: 'home',
    component: () => import('../views/HomeView.vue')
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/account/Login.vue')
  },
  {
    path: '/animal',
    name: 'animal',
    component: () => import('../views/management/animal/AnimalManage.vue')
  },
  {
    path: '/report',
    name: 'report',
    component: () => import('../views/management/report/ReportManage.vue')
  },
  {
    path: '/volunteer',
    name: 'volunteer',
    meta: { adminOnly: true },
    component: () => import('../views/management/volunteer/VolunteerManagerMain.vue')
  },
  {
    path: '/treatment',
    name: 'treatment',
    component: () => import('../views/management/treatment/AnimalTreatmentManage.vue')
  },
  {
    path: '/treatment/item',
    name: 'treatmentItemManage',
    component: () => import('../views/management/treatment/ItemManageMain.vue')
  },
  {
    path: '/adoption',
    name: 'adoption',
    component: () => import('../views/management/adoption/AdoptionManageMain.vue')
  },
  {
    path: '/news',
    component: () => import('../views/management/news/NewsManageMain.vue'),
    children: [
      { path: 'shelter-news', name: 'ShelterNewsManage', component: () => import('@/views/management/news/ShelterNewsManage.vue') },
      { path: 'popup',        name: 'PopupManage',       component: () => import('@/views/management/news/PopupManage.vue') },
    ]
  },
  {
    path: '/chat/:chat_room_idx',
    name: 'chat_detail',
    component: () => import('../views/management/chat/ChatDetail.vue')
  },
  {
    path: '/setting',
    name: 'setting',
    component: () => import('../views/setting/SettingMain.vue'),
    props: true,
    children: [
      { path: '', name: 'SettingMain', meta: { keepAlive: true }, redirect: { name: 'ShelterSetting' } },
      { path: 'shelter', name: 'ShelterSetting', meta: { keepAlive: true }, component: () => import('@/views/setting/ShelterSetting.vue') },
      { path: 'shelters', name: 'ShelterAdminManage', meta: { keepAlive: true, adminOnly: true }, component: () => import('@/views/setting/ShelterAdminManage.vue') },
      { path: 'kit', name: 'KitStockManage', meta: { keepAlive: true, adminOnly: true }, component: () => import('@/views/setting/KitStockManage.vue') },
      { path: 'health', name: 'HealthItemSetting', meta: { keepAlive: true }, component: () => import('@/views/setting/HealthItemSetting.vue') },
      { path: 'alarm', name: 'AlarmSetting', meta: { keepAlive: true }, component: () => import('@/views/setting/AlarmSetting.vue') },
      { path: 'phrase', name: 'PhraseManage', meta: { keepAlive: true }, component: () => import('@/views/management/shelter/PhraseManage.vue') },
      { path: 'treatment', name: 'TreatmentManageMain', meta: { keepAlive: true }, component: () => import('@/views/management/treatment/TreatmentManageMain.vue'), 
        children: [
          { path: '', name: 'TreatmentManage', meta: { keepAlive: true }, redirect: { name: 'TreatmentManage' } },
          { path: 'treatment', name: 'TreatmentManage', meta: { keepAlive: true }, component: () => import('@/views/management/treatment/TreatmentManage.vue') },
          { path: 'medicine', name: 'MedicineManage', meta: { keepAlive: true }, component: () => import('@/views/management/treatment/MedicineManage.vue') },
          { path: 'frequent', name: 'FrequentTreatmentManage', meta: { keepAlive: true }, component: () => import('@/views/management/treatment/FrequentTreatmentManage.vue') },
        ]
      },
    ]
  },

  {
    path: '/statistics',
    name: 'statistics',
    meta: { adminOnly: true },
    component: () => import('@/views/management/statistics/Statistics.vue')
  },

]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

//화면 이동 가드: 로그인 안 했으면 로그인 화면, 관리자 전용 화면(meta.adminOnly)은 관리자 계정만.
//화면 표시만 막는 것 — 실제 데이터 권한은 서버(ShelterAuthzInterceptor)가 로그인 토큰 기준으로 확인한다.
router.beforeEach((to) => {
  let auth = null;
  try { auth = JSON.parse(localStorage.getItem('auth_token')); } catch (e) { auth = null; }
  const loggedIn = !!(auth && auth.accessToken);

  if (!loggedIn) return to.name === 'login' ? true : { name: 'login' };
  if (to.matched.some(r => r.meta && r.meta.adminOnly)) {
    const email = auth.shelterAccount && auth.shelterAccount.shelter_device_number;
    if (email !== 'admin@pasm.kr') return { name: 'home' };
  }
  return true;
});

//새 버전 배포 확인: 메뉴를 옮길 때 서버 버전이 다르면 그 화면을 새로 불러옴 (첫 화면 진입은 제외)
router.beforeEach(async (to, from) => {
  if (from === START_LOCATION) return true;
  const latest = await newVersionAvailable();
  if (latest) { reloadTo(latest, router.resolve(to).href); return false; }
  return true;
});

//예전 화면에서 지워진 파일(chunk)을 못 받으면 가려던 화면으로 한 번 다시 불러옴
router.onError((err, to) => {
  if (isChunkLoadError(err)) reloadForChunkError(to ? router.resolve(to).href : null);
});

/*
export default function(store) {
  const router = createRouter({
    history: createWebHistory(),
    routes
  })
  router.beforeEach(async (to, from, next) => {
    if (import.meta.env.VITE_IS_LOGIN === 'Y') {
      const access = cookies.get('accessToken');
      const refresh = cookies.get('refreshToken');

      //@@ refreshToken이 없을 경우 로그인 창 띄우기
      if (refresh === null) {
        console.warn('need login...');
        store.commit('auth/needLogin', true);
      } else if (access === null && refresh !== null) { 
        //refreshToken은 있고 accessToken만 있을 경우 재발급요청
        await store.dispatch('auth/refreshToken');
      }else {
        //토큰이 다 있다면 페이지 이동 전 토큰 검증
        await store.dispatch('auth/verifyToken');
      }
      return next();
    } else {
      store.commit('auth/needLogin', false);
      return next()
    }
  })
  return router;
}
*/

export default router;

